import { useCallback, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { countries } from "country-data";

import AuctionExpiration from "./auctionExpiration";
import AuctionNavigation from "../auctionNavigation";
import AuctionRecords from "./auctionRecords";
import PlaceBid from "./placeBid";

import locationImage from "../../assets/location.png";
import FLAGS from "../../assets/Flags";

import auctionService from "../../services/auction";
import { W3Context } from "../../services/context";

import { formattedWallet } from "../../utils/account";

import "./auctionDetailsStyles.css";

const AuctionDetails = ({ onClickConnectWallet, GAS_FEE }) => {
  const { account } = useContext(W3Context);
  const { id: tokenId } = useParams();
  // TODO: set placeholders
  const [auction, setAuction] = useState(null);
  const [auctionNotFound, setAuctionNotFound] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [countryName, setCountryName] = useState("");
  const [countryImg, setCountryImg] = useState("");
  const [expirationInMilliseconds, setExpirationInMilliseconds] = useState(0);
  const [startInMilliseconds, setStartInMilliseconds] = useState(0);
  const [auctionHasStarted, setAuctionHasStarted] = useState(false);
  const [auctionHasEnded, setAuctionHasEnded] = useState(false);
  const [updateData, setUpdateData] = useState({});

  const initData = useCallback(() => {
    const { startDate, expirationDate } = auction;

    const eDate = expirationDate ? new Date(expirationDate) : null;
    const sDate = startDate ? new Date(startDate) : null;

    const _long =
      auction && auction.point && auction.point.length === 2
        ? auction.point[0]
        : "";

    setLongitude(_long);

    const _lat =
      auction && auction.point && auction.point.length === 2
        ? auction.point[1]
        : "";

    setLatitude(_lat);

    const expiration = eDate ? eDate.getTime() : 0;
    setExpirationInMilliseconds(expiration);

    const start = sDate ? sDate.getTime() : 0;
    setStartInMilliseconds(start);

    const now = new Date();

    const auctionEnded = expiration > 0 ? eDate <= now : false;
    setAuctionHasEnded(auctionEnded);

    const auctionStarted = start > 0 ? sDate <= now : false;
    setAuctionHasStarted(auctionStarted);

    const country = countries[auction.countryCode]
      ? countries[auction.countryCode].name
      : auction.countryCode;

    setCountryName(country);

    if (auction.countryCode) {
      setCountryImg(FLAGS(auction.countryCode.toLowerCase()));
    }
  }, [auction]);

  const getData = useCallback(async () => {
    try {
      if (account) {
        const data = await auctionService.getDetailsByTokenId(tokenId, {
          address: account,
        });
        setAuctionNotFound(false);
        setAuction(data);
      }
    } catch (error) {
      setAuctionNotFound(true);
    }
  }, [account, tokenId]);

  useEffect(() => {
    if (auction) {
      initData();
    }
  }, [auction, initData]);

  useEffect(() => {
    if (updateData) {
      getData();
    }
  }, [getData, updateData]);

  return (
    <Container className="auction-details">
      <AuctionNavigation onClickConnectWallet={onClickConnectWallet} />
      {auctionNotFound ? (
        <Row className="no-auction justify-content-center mx-auto">
          <Col xs={12} md={8}>
            <span>No nft with the provided token was found</span>
          </Col>
        </Row>
      ) : (
        <>
          {auction && (
            <Row className="justify-content-center mb-5">
              <Col xs={12} md={6}>
                <div className="viewer-video mx-auto">
                  <div className="video-body">
                    <video
                      style={{ borderRadius: "10px" }}
                      className="w-100"
                      src={auction.assetUrl}
                      alt="NFT"
                      type="video/mp4"
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <Row className="data-details justify-content-center">
                  <Col xs={12}>
                    <Row>
                      <span className="auction-name">{auction.name}</span>
                    </Row>
                    {auction.owner && (
                      <Row>
                        <span className="auction-info mr-1">
                          Owned by
                          <span className="highlight-purple">
                            {formattedWallet(auction.owner)}
                          </span>
                        </span>
                      </Row>
                    )}
                    <Row className="align-items-center mt-2">
                      <div>
                        <img
                          className="auction-country"
                          src={countryImg}
                          alt="flag"
                        />
                      </div>
                      <span className="auction-info">{countryName}</span>
                    </Row>
                    <Row className="align-items-center mt-2">
                      <div>
                        <img
                          className="location"
                          src={locationImage}
                          alt="location"
                        />
                        <span className="auction-info">
                          {latitude}, {longitude}
                        </span>
                      </div>
                    </Row>
                    <Row className="auction-box-data mt-3">
                      <AuctionExpiration
                        target={
                          auctionHasStarted
                            ? expirationInMilliseconds
                            : startInMilliseconds
                        }
                        hasStarted={auctionHasStarted}
                      />
                    </Row>
                    <Row className="auction-box-data mt-2">
                      <PlaceBid
                        auctionId={auction._id}
                        bestBid={auction.bestBid}
                        bids={auction.bids}
                        initialValue={auction.initialValue}
                        tokenId={auction.tokenId}
                        setUpdateData={setUpdateData}
                        auctionState={auction.state}
                        hasStarted={auctionHasStarted}
                        hasEnded={auctionHasEnded}
                        auctionOwner={auction.owner}
                        isHottest={auction.isHottest}
                        userHasBids={auction.userHasBids}
                        auctioneerState={auction.auctioneerState}
                        GAS_FEE={GAS_FEE}
                      />
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {auction && (
            <Row className="justify-content-center mb-3">
              <Col xs={12}>
                <AuctionRecords records={auction.bids} />
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default AuctionDetails;
